<template>
  <v-dialog v-model="show" max-width="600" persistent>
    <v-card>
      <!-- Header -->
      <v-card-title class="d-flex justify-space-between align-start pb-1 pt-5 px-6">
        <div>
          <div class="caption font-weight-medium" style="color: #64748b; letter-spacing: 0.05em;">ÁREA DE AÇÃO</div>
          <div class="title font-weight-bold" style="color: #1e293b;">
            {{ municipio ? `${municipio.nome} — ${municipio.sigla}` : '' }}
          </div>
        </div>
        <v-btn icon @click="fechar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="px-6 pt-6 pb-2">
        <!-- Código IBGE + UF -->
        <div class="info-box mb-6">
          <span class="body-2"><strong>Código IBGE:</strong> {{ municipio ? municipio.id : '' }}</span>
          <span class="body-2 ml-6"><strong>UF:</strong> {{ municipio ? municipio.sigla : '' }}</span>
        </div>

        <!-- Datas -->
        <v-row>
          <v-col cols="6">
            <div class="label-field">Data Início</div>
            <v-text-field
              v-model="form.dtInicio"
              type="date"
              outlined
              hide-details
              class="campo-full-width"
              :error="!!erroDataInicio"
              :disabled="!podeEditar"
            />
            <div v-if="erroDataInicio" class="erro-validacao">{{ erroDataInicio }}</div>
          </v-col>
          <v-col cols="6">
            <div class="label-field">Data Fim</div>
            <v-text-field
              v-model="form.dtFim"
              type="date"
              outlined
              hide-details
              class="campo-full-width"
              :error="!!erroDataFim"
              :disabled="!podeEditar"
            />
            <div v-if="erroDataFim" class="erro-validacao">{{ erroDataFim }}</div>
          </v-col>
        </v-row>

        <!-- Observações -->
        <div class="label-field mt-2">Observações</div>
        <v-textarea
          v-model="form.observacao"
          outlined
          rows="3"
          hide-details
          class="campo-full-width observacao-textarea"
          placeholder="Digite observações ou anotações sobre este município..."
          no-resize
          :disabled="!podeEditar"
        />

        <!-- Anexos -->
        <div class="label-field mt-6">Anexos</div>

        <!-- Arquivo já salvo -->
        <div v-if="form.possuiAnexo" class="anexo-existente">
          <v-icon color="primary1" size="20">mdi-paperclip</v-icon>
          <span class="body-2 ml-2 mr-auto">{{ form.nomeAnexo }}</span>
          <v-btn icon small color="primary1" @click="downloadAnexo" title="Download">
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn v-if="podeEditar" icon small color="error" @click="showConfirmRemocao = true" title="Remover">
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>

        <!-- Drop zone -->
        <div
          v-else-if="podeEditar"
          class="drop-zone"
          :class="{ 'drop-zone--over': isDragging }"
          @click="$refs.fileInput.click()"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <input
            ref="fileInput"
            type="file"
            style="display:none"
            :accept="tiposAceitos"
            @change="onFileChange"
          />
          <v-icon size="28" style="color: #94a3b8;">mdi-upload</v-icon>
          <div class="body-2 mt-1" style="color: #64748b;">Clique para selecionar arquivos ou arraste aqui</div>
          <div class="caption" style="color: #94a3b8;">PDF, DOC, XLS, PNG, JPG</div>
        </div>

        <!-- Preview do arquivo selecionado (antes de salvar) -->
        <div v-if="form.arquivo" class="arquivo-preview mt-2">
          <v-icon small color="primary1">mdi-file-outline</v-icon>
          <span class="caption ml-1">{{ form.arquivo.name }}</span>
          <v-btn icon x-small @click="form.arquivo = null">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-text>

      <v-divider class="mt-4" />

      <!-- Botões -->
      <v-card-actions class="px-6 pb-5 pt-4">
        <v-spacer />
        <v-btn outlined rounded @click="fechar" class="mr-2 btn-cancelar">{{ podeEditar ? 'Cancelar' : 'Fechar' }}</v-btn>
        <v-btn v-if="podeEditar" rounded color="#00995d" dark :loading="salvando" @click="salvar" class="btn-salvar">Salvar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmação remoção -->
    <ExcluirModal
      :showDelete="showConfirmRemocao"
      :text="'Confirma a remoção do anexo?'"
      @close="showConfirmRemocao = false"
      @confirmDelete="removerAnexo"
    />

    <SucessoModal
      :showSucess="showSucesso"
      :sucessType="'generic'"
      :genericTitle="'Sucesso!'"
      :genericMessage="mensagemSucesso"
      @close="onFecharSucesso"
    />

    <ErrorModal v-model="showErro" :errObj="errObj" />
  </v-dialog>
</template>

<script>
import ErrorModal from '@/components/Shared/ErrorModal/ErrorModal'

export default {
  components: { ErrorModal },
  props: {
    value: { type: Boolean, default: false },
    municipio: { type: Object, default: null },
    podeEditar: { type: Boolean, default: false },
    service: { type: Object, required: true }
  },
  data() {
    return {
      show: this.value,
      form: {
        dtInicio: '',
        dtFim: '',
        observacao: '',
        possuiAnexo: false,
        nomeAnexo: null,
        arquivo: null
      },
      erroDataInicio: '',
      erroDataFim: '',
      isDragging: false,
      salvando: false,
      showConfirmRemocao: false,
      showSucesso: false,
      mensagemSucesso: '',
      fecharAoConfirmarSucesso: false,
      showErro: false,
      errObj: {},
      tiposAceitos: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg'
    }
  },
  watch: {
    value(v) {
      this.show = v
      if (v && this.municipio) this.carregar()
    },
    show(v) {
      if (!v) this.$emit('input', false)
    }
  },
  mounted() {
    if (this.value && this.municipio) this.carregar()
  },
  methods: {
    async carregar() {
      this.form = { dtInicio: '', dtFim: '', observacao: '', possuiAnexo: false, nomeAnexo: null, arquivo: null }
      this.erroDataInicio = ''
      this.erroDataFim = ''
      try {
        const res = await this.service.getInfoComplementar(this.municipio.id)
        const d = res.data
        if (d && d.id) {
          this.form.dtInicio = d.dtInicio ? this.brParaInput(d.dtInicio) : ''
          this.form.dtFim = d.dtFim ? this.brParaInput(d.dtFim) : ''
          this.form.observacao = d.observacao || ''
          this.form.possuiAnexo = d.possuiAnexo
          this.form.nomeAnexo = d.nomeAnexo
        }
      } catch {
        // sem dados ainda
      }
    },
    async salvar() {
      this.erroDataInicio = ''
      this.erroDataFim = ''

      if (this.form.dtFim && this.form.dtInicio && this.form.dtFim < this.form.dtInicio) {
        this.erroDataFim = 'Data de Fim não pode ser anterior à Data de Início.'
        return
      }

      this.salvando = true
      try {
        await this.service.putInfoComplementar(this.municipio.id, {
          dtInicio: this.inputParaBr(this.form.dtInicio),
          dtFim: this.inputParaBr(this.form.dtFim),
          observacao: this.form.observacao || null
        })

        if (this.form.arquivo) {
          await this.service.postAnexo(this.municipio.id, this.form.arquivo)
          this.form.possuiAnexo = true
          this.form.nomeAnexo = this.form.arquivo.name
          this.form.arquivo = null
        }

        this.mensagemSucesso = 'Informações salvas com sucesso!'
        this.fecharAoConfirmarSucesso = true
        this.showSucesso = true
      } catch (err) {
        this.errObj = { status: err?.response?.status, error: err?.response?.data?.message }
        this.showErro = true
      } finally {
        this.salvando = false
      }
    },
    async downloadAnexo() {
      try {
        const res = await this.service.getAnexo(this.municipio.id)
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.form.nomeAnexo)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (err) {
        this.errObj = { status: err?.response?.status, error: 'Erro ao baixar o arquivo.' }
        this.showErro = true
      }
    },
    async removerAnexo() {
      this.showConfirmRemocao = false
      try {
        await this.service.deleteAnexo(this.municipio.id)
        this.form.possuiAnexo = false
        this.form.nomeAnexo = null
        this.mensagemSucesso = 'Anexo removido com sucesso!'
        this.showSucesso = true
      } catch (err) {
        this.errObj = { status: err?.response?.status, error: err?.response?.data?.message }
        this.showErro = true
      }
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) this.form.arquivo = file
    },
    onDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file) this.form.arquivo = file
    },
    fechar() {
      this.show = false
      this.$emit('input', false)
    },
    onFecharSucesso() {
      this.showSucesso = false
      if (this.fecharAoConfirmarSucesso) {
        this.fecharAoConfirmarSucesso = false
        this.fechar()
      }
    },
    brParaInput(dataBr) {
      // "dd/MM/yyyy" → "yyyy-MM-dd"
      if (!dataBr) return ''
      const p = dataBr.split('/')
      return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : dataBr
    },
    inputParaBr(dataInput) {
      // "yyyy-MM-dd" → "dd/MM/yyyy"
      if (!dataInput) return null
      const p = dataInput.split('-')
      return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : dataInput
    }
  }
}
</script>

<style lang="scss" scoped>
.info-box {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 10px 14px;
  display: inline-flex;
  gap: 0;
  color: #60656c;

  strong {
    color: #60656c;
  }
}

.label-field {
  font-size: 17px;
  font-weight: 700;
  color: #60656c;
  margin-bottom: 8px;
}

.campo-full-width {
  width: 100%;
  max-width: 100%;
  flex-grow: 1;
}
.campo-full-width ::v-deep .v-input__control,
.campo-full-width ::v-deep .v-input__slot,
.campo-full-width ::v-deep .v-text-field__slot,
.campo-full-width ::v-deep .v-textarea__slot {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  flex-grow: 1 !important;
}
.campo-full-width ::v-deep input,
.campo-full-width ::v-deep textarea {
  width: 100% !important;
  max-width: none !important;
  min-width: 0 !important;
  flex-grow: 1 !important;
}

// Mensagem de validação das datas (ex.: "Data de Fim não pode ser anterior à Data
// de Início."), renderizada manualmente em vez de usar :error-messages do Vuetify:
// dentro dessa modal, a área de mensagens do Vuetify (.v-messages) fica sujeita ao
// mesmo vazamento global de CSS do table.sass que já afeta .v-input__slot/.v-input__control
// (ver memória bug-tablesass-vazamento-global-css), o que a empurrava pra fora do
// fluxo normal em vez de aparecer abaixo do campo. Um <div> simples, sem depender
// de nenhuma classe interna do Vuetify, garante a posição abaixo do campo e a quebra
// de linha independente desse vazamento.
.erro-validacao {
  width: 100%;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #ff5252;
  white-space: normal;
  word-break: break-word;
}

.observacao-textarea ::v-deep .v-input__control {
  height: auto !important;
  width: 100% !important;
}
.observacao-textarea ::v-deep .v-input__slot {
  min-height: 38px !important;
  width: 100% !important;
  padding: 10px 12px !important;
  align-items: flex-start;
}
.observacao-textarea ::v-deep textarea {
  min-height: 18px !important;
  margin-top: 0 !important;
  padding-top: 0;
  line-height: 1.5;
}

.drop-zone {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 28px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover, &--over {
    border-color: #00995d;
    background: #f1faf6;
  }
}

.arquivo-preview {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 4px 8px;
}

.anexo-existente {
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 6px;
  padding: 8px 12px;
}

.btn-cancelar {
  color: #212121 !important;
  border-color: #bdbdbd !important;
  padding: 0 24px;
}

.btn-salvar {
  padding: 0 32px;
}
</style>
